import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaBook, FaGraduationCap, FaMapMarkerAlt } from "react-icons/fa";
import "./StateSyllabus.css";
import { ENQUIRY_URL } from "../config";

const StateSyllabus = () => {
  const partASubjects = [
    "Mathematics",
    "Science",
    "Social Science",
    "Kannada (1st Language)",
    "English (2nd Language)",
    "Hindi (3rd Language)",
    "Sanskrit (1st Language option)",
  ];

  const partBSubjects = [
    "Music",
    "Computer Science",
    "Physical Education",
    "Craft",
  ];

  const highlights = [
    {
      icon: "🏛️",
      title: "Available Since",
      value: "1966",
      description: "Sri Vani's first steps as a school began with State Syllabus",
    },
    {
      icon: "🏛️",
      title: "Affiliated To",
      value: "KSEEB",
      description: "Karnataka Secondary Education Examination Board",
    },
    {
      icon: "📚",
      title: "Standards",
      value: "8th – 10th",
      description: "From 8th to 10th Standard",
    },
    {
      icon: "🏫",
      title: "Campuses",
      value: "1 Campus",
      description: "Rajajinagar",
    },
  ];

  const campuses = [
    {
      name: "Rajajinagar Campus",
      subtitle: "Ramavana",
      tagline: "A Burgeoning Campus that Vibrates Sentience.",
      image: "/images/state-syllabus/rajaji-nagar-campus.jpg",
      link: "/campuses/rajajinagar",
    },
  ];

  return (
    <div className="state-syllabus-page">
      {/* Page Header */}
      <section
        className="ss-page-header"
        style={{ backgroundImage: "url('/images/state-syllabus/state-board-curriculum.jpg')" }}
      >
        <div className="ss-header-overlay" />
        <div className="container">
          <motion.div
            className="ss-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="ss-header-tag">Karnataka Secondary Education Examination Board</p>
            <h1>State Syllabus</h1>
            <p className="ss-header-sub">A New Paradigm Of Education</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Highlights */}
      <section className="ss-highlights-section">
        <div className="container">
          <div className="ss-highlights-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="ss-highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-highlight-icon">{item.icon}</div>
                <div className="ss-highlight-value">{item.value}</div>
                <div className="ss-highlight-title">{item.title}</div>
                <div className="ss-highlight-desc">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emblem + Bullets Section */}
      <section className="ss-emblem-section">
        <div className="container">
          <div className="ss-emblem-row">
            <motion.div
              className="ss-emblem-col"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/state-syllabus/state-syllabus-hero.jpeg"
                alt="Karnataka State Emblem"
                className="ss-emblem-img"
              />
            </motion.div>
            <motion.div
              className="ss-bullets-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="ss-key-bullets">
                <li>Available at Sri Vani Since 1966</li>
                <li>Affiliated to KSEEB</li>
                <li>From 8th to 10th Standard Only</li>
                <li>Available at Rajajinagar Campus</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="ss-description-section">
        <div className="container">
          <motion.div
            className="ss-description-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              KSEEB, the main education board in Karnataka, was established in 1964
              and has conducted SSLC and PUC exams ever since. SVECT's first steps
              as a school began just two years later in 1966 and prescribed to the
              State syllabus.
            </p>
            <p>
              The Karnataka Education Board decides on the syllabus and courses
              after conducting extensive research, and it is designed to help
              students get familiar with all of the major themes from all of the
              subjects.
            </p>
            <p>
              The wealth of experience here at SVECT and its consistency in
              securing centum results year after year makes the institution an
              ideal bet for those opting for this curriculum.
            </p>
            <div className="ss-desc-cta">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="ss-subjects-section section">
        <div className="container">
          <motion.div
            className="ss-subjects-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="ss-section-title">8th, 9th &amp; 10th Standard</h2>
            <p className="ss-section-subtitle">
              Comprehensive subject coverage across all standards
            </p>
          </motion.div>

          <div className="ss-subjects-grid">
            <motion.div
              className="ss-subject-block"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="ss-subject-block-header">
                <FaGraduationCap />
                <h3>Part A Subjects</h3>
              </div>
              <ul className="ss-subject-list">
                {partASubjects.map((subject, idx) => (
                  <li key={idx}>{subject}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="ss-subject-block"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <div className="ss-subject-block-header">
                <FaBook />
                <h3>Part B Subjects</h3>
              </div>
              <ul className="ss-subject-list">
                {partBSubjects.map((subject, idx) => (
                  <li key={idx}>{subject}</li>
                ))}
              </ul>
              <div className="ss-subject-note">
                <p>
                  Students select from Part B subjects based on availability and
                  aptitude to complement their core Part A curriculum.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Campuses Section */}
      <section className="ss-campuses-section section">
        <div className="container">
          <motion.div
            className="ss-campuses-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="ss-section-tag">
              <FaMapMarkerAlt /> Pursue at following Campuses
            </div>
            <h2 className="ss-section-title">Our State Syllabus Campuses</h2>
          </motion.div>

          <div className="ss-campuses-grid">
            {campuses.map((campus, index) => (
              <motion.div
                key={index}
                className="ss-campus-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="ss-campus-image">
                  <img src={campus.image} alt={campus.name} />
                  <div className="ss-campus-overlay">
                    <span>{campus.subtitle}</span>
                  </div>
                </div>
                <div className="ss-campus-info">
                  <h3>{campus.name}</h3>
                  <p>{campus.tagline}</p>
                  <Link to={campus.link} className="btn btn-outline ss-explore-btn">
                    Explore Campus
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ss-cta-section">
        <div className="container">
          <motion.div
            className="ss-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Begin Your State Syllabus Journey at Sri Vani</h2>
            <p>
              Join a legacy of academic excellence that has been shaping
              students since 1966.
            </p>
            <div className="ss-cta-buttons">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply for Admission
              </a>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StateSyllabus;
